import React, { useState } from 'react';
import { Box, Paper, Typography, TextField, Stack, Button, MenuItem, Select, FormControl, InputLabel, Alert } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import axios from 'axios';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';

const TEMPLATES = {
  custom: "{\n  \"event\": \"custom.event\",\n  \"data\": {}\n}",
  stripe: "{\n  \"id\": \"evt_123\",\n  \"object\": \"event\",\n  \"type\": \"payment_intent.succeeded\",\n  \"data\": {\n    \"object\": {\n      \"amount\": 2000,\n      \"currency\": \"usd\",\n      \"status\": \"succeeded\"\n    }\n  }\n}",
  github: "{\n  \"ref\": \"refs/heads/main\",\n  \"repository\": {\n    \"name\": \"example-repo\",\n    \"full_name\": \"user/example-repo\"\n  },\n  \"pusher\": {\n    \"name\": \"user\"\n  }\n}",
  slack: "{\n  \"text\": \"Hello, this is a test message from Webhook Tester!\"\n}"
};

function WebhookTester() {
  const [url, setUrl] = useState('');
  const [template, setTemplate] = useState('custom');
  const [payload, setPayload] = useState(TEMPLATES.custom);
  const [status, setStatus] = useState(null); // null, 'loading', 'success', 'error'
  const [response, setResponse] = useState(null);

  const handleTemplateChange = (e) => {
    const val = e.target.value;
    setTemplate(val);
    setPayload(TEMPLATES[val]);
  };

  const handleSend = async () => {
    if (!url) {
      alert("Please enter a webhook destination URL.");
      return;
    }
    
    let parsedData;
    try {
      parsedData = JSON.parse(payload);
    } catch (e) {
      alert("Invalid JSON payload.");
      return;
    }

    setStatus('loading');
    setResponse(null);

    try {
      const res = await axios.post(url, parsedData, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 10000
      });
      setStatus('success');
      setResponse({ status: res.status, data: res.data });
    } catch (err) {
      setStatus('error');
      setResponse({
        status: err.response?.status || 'Network Error',
        data: err.response?.data || err.message
      });
    }
  };

  return (
    <>
      <SEO
        title="Webhook Tester | Send Mock Webhooks | UmbrellaCo"
        description="Send mock webhook payloads to test your API integrations. Use templates for Stripe, GitHub, and Slack, or fire custom JSON payloads."
        keywords="webhook tester, send webhooks, mock webhook payload, test stripe webhook, test github webhook"
        url="/api-tools/webhook-tester"
        jsonLd={createSoftwareAppSchema(
          'Webhook Tester',
          'Send mock webhook payloads to test API integrations.',
          'https://umbrellaco.in/api-tools/webhook-tester',
          ['Webhook Testing', 'API Integration', 'Mock Data']
        )}
      />
      <ToolPageContainer
        title="Webhook Sender / Tester"
        description="Dispatch mock webhook events to your own server or webhook.site to verify your integration logic. Note: CORS restrictions may prevent sending to some servers directly from the browser."
      >

      <Box sx={{ display: 'flex', gap: 3, flexGrow: 1, minHeight: 0 }}>
        <Paper sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}>
          <TextField
            label="Destination Webhook URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://webhook.site/..."
            fullWidth
            variant="outlined"
          />

          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Mock Payload Template</InputLabel>
              <Select value={template} label="Mock Payload Template" onChange={handleTemplateChange}>
                <MenuItem value="custom">Custom</MenuItem>
                <MenuItem value="stripe">Stripe Payment</MenuItem>
                <MenuItem value="github">GitHub Push</MenuItem>
                <MenuItem value="slack">Slack Message</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <TextField
            label="JSON Payload"
            multiline
            rows={12}
            value={payload}
            onChange={(e) => setPayload(e.target.value)}
            variant="outlined"
            fullWidth
            sx={{ flexGrow: 1, '& .MuiOutlinedInput-root': { height: '100%', fontFamily: 'monospace' }, '& textarea': { height: '100% !important' } }}
          />

          <Button 
            variant="contained" 
            size="large" 
            onClick={handleSend} 
            disabled={status === 'loading'}
            startIcon={<SendIcon />}
          >
            {status === 'loading' ? 'Sending...' : 'Fire Webhook'}
          </Button>
        </Paper>

        <Paper sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', bgcolor: 'background.default', overflowY: 'auto' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Response</Typography>
          
          {status === null && (
            <Alert severity="info">Enter a URL and fire a webhook to see the response.</Alert>
          )}

          {status === 'loading' && (
            <Alert severity="info">Sending request...</Alert>
          )}

          {response && (
            <Box>
              <Alert severity={status === 'success' ? 'success' : 'error'} sx={{ mb: 2 }}>
                HTTP Status: {response.status}
              </Alert>
              <Typography variant="subtitle2">Response Body:</Typography>
              <Box sx={{ p: 2, mt: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {typeof response.data === 'object' ? JSON.stringify(response.data, null, 2) : String(response.data)}
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
      </ToolPageContainer>
    </>
  );
}

export default WebhookTester;
